import React, { useMemo } from 'react';
import UserFilterSelect from '@/shared/components/UserFilterSelect';
import TaskTypeFilter from '@/features/tasks/components/TaskTypeFilter';
import StatusFilterSelect from '../StatusFilterSelect';
import { countTasksByStatus } from '@/features/tasks/utils/statusFilter';
import {
    AGENTIC_FILTER_AGENTIC,
    AGENTIC_FILTER_ALL,
    isAgenticOnlyFilter,
} from '@/features/tasks/utils/taskTypeFilter';
import { Button, Search, SegmentedControlIcon, SparkIcon, Toggle } from '@spark';
import './HomePageFilters.css';

const VIEW_TOGGLE_ITEMS = [
    { id: 'cards', label: 'Cards view', iconSlug: 'table' },
    { id: 'list', label: 'List view', iconSlug: 'layout-rows' },
];

const FILTER_ALL = 'All';

const isSelectedFilter = (value) => {
    if (value == null || value === FILTER_ALL) return false;
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
};

export const hasActiveHomePageFilters = ({
    searchValue,
    taskType,
    agenticFilter,
    userFilter,
    statusFilter,
}) => (
    Boolean(searchValue)
    || isSelectedFilter(taskType)
    || isAgenticOnlyFilter(agenticFilter)
    || isSelectedFilter(userFilter)
    || isSelectedFilter(statusFilter)
);

export const HomePageFilters = ({
    searchValue,
    setSearchValue,
    taskType,
    setTaskType,
    agenticFilter = AGENTIC_FILTER_ALL,
    setAgenticFilter,
    userFilter,
    setUserFilter,
    statusFilter,
    setStatusFilter,
    allUsers,
    allApps,
    viewType,
    setViewType,
}) => {
    const statusCounts = useMemo(() => countTasksByStatus(allApps), [allApps]);
    const agenticOnly = isAgenticOnlyFilter(agenticFilter);
    const hasActiveFilters = hasActiveHomePageFilters({
        searchValue,
        taskType,
        agenticFilter,
        userFilter,
        statusFilter,
    });

    const handleClearAllFilters = () => {
        setSearchValue('');
        setTaskType(FILTER_ALL);
        setAgenticFilter?.(AGENTIC_FILTER_ALL);
        setUserFilter(FILTER_ALL);
        setStatusFilter(FILTER_ALL);
    };

    const clearAllClassName = [
        'home-page-filters__clear-all',
        hasActiveFilters ? '' : 'home-page-filters__clear-all--hidden',
    ].filter(Boolean).join(' ');

    return (
        <div className="home-page-filters-bar">
            <div className="home-page-filters fs-unmask">
                <div className="home-page-filters__search">
                    <Search
                        size="medium"
                        placeholder="Search projects & tasks"
                        iconPosition="start"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        showClearButton
                        onClear={() => setSearchValue('')}
                    />
                </div>
                <TaskTypeFilter
                    value={taskType}
                    onChange={setTaskType}
                    agentic={agenticOnly}
                    placeholder="All Task Types"
                />
                <UserFilterSelect
                    users={allUsers || []}
                    value={userFilter}
                    onChange={setUserFilter}
                    placeholder="Filter by user"
                    allValue="All"
                />
                <StatusFilterSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allValue="All"
                    placeholder="Filter by status"
                    statusCounts={statusCounts}
                />
                <Toggle
                    id="home-page-filters-agentic"
                    className="home-page-filters__agentic"
                    size="small"
                    label="Agentic"
                    labelPosition="start"
                    active={agenticOnly}
                    onChange={(event) => setAgenticFilter?.(
                        event.target.checked ? AGENTIC_FILTER_AGENTIC : AGENTIC_FILTER_ALL,
                    )}
                />
                <Button
                    className={clearAllClassName}
                    variant="ghost"
                    size="small"
                    startIcon={<SparkIcon slug="close" size="small" />}
                    onClick={handleClearAllFilters}
                    tabIndex={hasActiveFilters ? undefined : -1}
                    aria-hidden={!hasActiveFilters}
                >
                    Clear All
                </Button>
            </div>
            <div className="home-page-filters__actions fs-unmask">
                <SegmentedControlIcon
                    size="medium"
                    items={VIEW_TOGGLE_ITEMS}
                    activeId={viewType === 'list' ? 'list' : 'cards'}
                    onChange={setViewType}
                />
            </div>
        </div>
    );
};
